import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { checkUsernameAvailable, login, register } from '../api/auth';
import type { RegisterContact, UsernameAvailability } from '../api/auth';
import { ApiError } from '../api/client';
import { getAppSettings } from '../api/settings';
import { imageSrc } from '../api/media';
import { createSupportTicket } from '../api/support';
import { backdropClose } from '../lib/modalBackdrop';
import QrLogin from './QrLogin';
import { setLanguagePref } from '../i18n';
import { LANGUAGES } from '../locales';

const USERNAME_RE = /^[a-zA-Z0-9_]{3,32}$/;

export default function Login({ onAuthed }: { onAuthed: () => void }) {
  const { t, i18n } = useTranslation('auth');
  const [mode, setMode] = useState<'login' | 'register'>('login');
  // Вход по QR вместо формы (только в режиме входа).
  const [qrMode, setQrMode] = useState(false);
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // Оба поля опциональны по отдельности — обязательно хотя бы одно (можно и оба сразу,
  // недостающее потом добавляется в настройках профиля).
  const [emailValue, setEmailValue] = useState('');
  const [phoneValue, setPhoneValue] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // 2FA (Google Authenticator): сервер ответил 428 — нужен код. Поле кода появляется после
  // первого успешного ввода логина/пароля, повторная отправка идёт с ним.
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  // Публичная форма обращения в поддержку — без авторизации (см. api/support.ts), для случаев
  // «не могу войти». Почта обязательна — на неё пришлют ответ.
  const [ticketOpen, setTicketOpen] = useState(false);
  const [ticketEmail, setTicketEmail] = useState('');
  const [ticketMessage, setTicketMessage] = useState('');
  const [ticketBusy, setTicketBusy] = useState(false);
  const [ticketMsg, setTicketMsg] = useState('');
  const [ticketError, setTicketError] = useState('');
  const sendTicket = async () => {
    setTicketBusy(true);
    setTicketMsg('');
    setTicketError('');
    try {
      await createSupportTicket(ticketEmail, ticketMessage);
      setTicketMsg(t('support.ticketSent'));
      setTicketEmail('');
      setTicketMessage('');
    } catch (err) {
      setTicketError(err instanceof ApiError ? err.message : t('errors.ticketSend'));
    } finally {
      setTicketBusy(false);
    }
  };
  const [availability, setAvailability] = useState<UsernameAvailability | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  // Имя/иконка приложения — редактируются в админ-панели (см. api/settings.ts), запасной
  // вариант — дефолтные "HomeChat"/favicon.png, если ещё не подгрузились или не заданы.
  const [appName, setAppName] = useState('HomeChat');
  const [appIcon, setAppIcon] = useState('/favicon.png');
  useEffect(() => {
    let alive = true;
    getAppSettings()
      .then((s) => {
        if (!alive) return;
        setAppName(s.appName);
        if (s.iconFileId) imageSrc(s.iconFileId).then((src) => alive && setAppIcon(src)).catch(() => {});
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  // Живая проверка занятости логина по мере ввода (debounce 400 мс) — как поиск сообщений
  // в чате (ChatView.tsx), только без отдельной кнопки: результат просто появляется под полем.
  useEffect(() => {
    if (mode !== 'register' || !USERNAME_RE.test(username)) {
      setAvailability(null);
      setCheckingUsername(false);
      return;
    }
    setCheckingUsername(true);
    const timer = window.setTimeout(async () => {
      try {
        const res = await checkUsernameAvailable(username, displayName || undefined);
        setAvailability(res);
      } catch {
        setAvailability(null);
      } finally {
        setCheckingUsername(false);
      }
    }, 400);
    return () => window.clearTimeout(timer);
  }, [username, displayName, mode]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (mode === 'register') {
      if (!USERNAME_RE.test(username)) {
        setError(t('validation.usernameFormat'));
        return;
      }
      if (availability && !availability.available) {
        setError(t('validation.usernameTaken'));
        return;
      }
      if (password !== confirmPassword) {
        setError(t('validation.passwordMismatch'));
        return;
      }
      if (password.length < 8) {
        setError(t('validation.passwordTooShort'));
        return;
      }
      if (!emailValue.trim() && !phoneValue.trim()) {
        setError(t('validation.contactRequired'));
        return;
      }
    }
    const wasRegistering = mode === 'register';
    setBusy(true);
    try {
      if (wasRegistering) {
        const contact: RegisterContact = {
          email: emailValue.trim() || undefined,
          phone: phoneValue.trim() || undefined,
        };
        await register(username, displayName, password, contact);
        // Аккаунт уже создан — дальше ведём себя как обычный логин. Если следующий
        // await login() упадёт по сети, повторный сабмит не должен звать register()
        // ещё раз с тем же логином (получили бы "логин уже занят" вместо повторного входа).
        setMode('login');
      }
      await login(username.trim(), password, twoFactorRequired ? twoFactorCode : undefined);
      onAuthed();
    } catch (err) {
      // 428 — у пользователя включена 2FA, без кода из Google Authenticator не пустить.
      if (err instanceof ApiError && err.status === 428) {
        setTwoFactorRequired(true);
        setError(t('errors.twoFactorRequired'));
      } else {
        setTwoFactorRequired(false);
        setError(
          wasRegistering
            ? t('errors.accountCreatedLoginFailed')
            : err instanceof ApiError ? err.message : t('errors.connection'),
        );
      }
    } finally {
      setBusy(false);
    }
  };

  const passType = showPassword ? 'text' : 'password';
  // Живая проверка совпадения паролей (при регистрации)
  const matchHint =
    mode === 'register' && confirmPassword.length > 0
      ? password === confirmPassword
        ? t('validation.match')
        : t('validation.noMatch')
      : null;

  const usernameHint = mode === 'register' && username && (checkingUsername || availability) ? (
    <div className="username-hint">
      {checkingUsername ? (
        <span style={{ color: 'var(--muted)' }}>{t('validation.usernameChecking')}</span>
      ) : !USERNAME_RE.test(username) ? null : availability?.available ? (
        <span style={{ color: 'var(--success)' }}>{t('validation.usernameAvailable')}</span>
      ) : availability ? (
        <>
          <span style={{ color: 'var(--error)' }}>{t('validation.usernameUnavailable')}</span>
          {availability.suggestions.length > 0 && (
            <div className="username-suggestions">
              {availability.suggestions.map((s) => (
                <button type="button" key={s} className="username-suggestion" onClick={() => setUsername(s)}>
                  {s}
                </button>
              ))}
            </div>
          )}
        </>
      ) : null}
    </div>
  ) : null;

  return (
    <div className="center-screen">
      <div className="card">
        <div className="logo">
          <img src={appIcon} alt={appName} />
          <div>
            <h1>{appName}</h1>
            <div className="sub">{t('brand.subtitle')}</div>
          </div>
        </div>

        {qrMode ? (
          <QrLogin onAuthed={onAuthed} />
        ) : (
        <form className="form" onSubmit={submit}>
          {mode === 'register' && (
            <input
              className="input"
              placeholder={t('fields.displayName')}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
            />
          )}
          <input
            className="input"
            placeholder={t(mode === 'login' ? 'fields.login' : 'fields.registerUsername')}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            required
          />
          {usernameHint}
          {mode === 'register' && (
            <>
              <input
                className="input"
                type="email"
                placeholder={t('fields.emailOptional')}
                value={emailValue}
                onChange={(e) => setEmailValue(e.target.value)}
                autoComplete="email"
              />
              <input
                className="input"
                type="tel"
                placeholder={t('fields.phoneOptional')}
                value={phoneValue}
                onChange={(e) => setPhoneValue(e.target.value)}
                autoComplete="tel"
              />
              {!emailValue.trim() && !phoneValue.trim() && (
                <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>{t('fields.contactRequired')}</div>
              )}
            </>
          )}
          <div className="password-wrap">
            <input
              className="input"
              type={passType}
              placeholder={t('fields.password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              required
            />
            <button type="button" className="eye-btn" onClick={() => setShowPassword(!showPassword)} aria-pressed={showPassword} title={t('actions.showPassword')}>
              <img src={showPassword ? '/eye-off.svg' : '/eye.svg'} alt="" />
            </button>
          </div>
          {mode === 'register' && (
            <>
              <div className="password-wrap">
                <input
                  className="input"
                  type={passType}
                  placeholder={t('fields.confirmPassword')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </div>
              {matchHint && (
                <div style={{ fontSize: 13, color: password === confirmPassword ? 'var(--success)' : 'var(--error)' }}>
                  {matchHint}
                </div>
              )}
            </>
          )}
          {twoFactorRequired && (
            <input
              className="input"
              inputMode="numeric"
              placeholder={t('fields.twoFactorCode')}
              value={twoFactorCode}
              onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              autoFocus
              required
            />
          )}
          {error && <div className="error">{error}</div>}
          <button className="btn" disabled={busy}>
            {mode === 'login' ? t('actions.login') : t('actions.createAccount')}
          </button>
        </form>
        )}

        {mode === 'login' && (
          <div style={{ marginTop: 14, textAlign: 'center' }}>
            <button className="link-btn" onClick={() => setQrMode(!qrMode)}>
              {qrMode ? t('actions.passwordLogin') : t('actions.qrLogin')}
            </button>
          </div>
        )}

        {!qrMode && (
        <div style={{ marginTop: 6, textAlign: 'center' }}>
          <button
            className="link-btn"
            onClick={() => {
              setMode(mode === 'login' ? 'register' : 'login');
              setError('');
              // Запрошенный при входе 2FA-код нерелевантен в форме регистрации — без сброса
              // поле кода оставалось видимым после переключения режима.
              setTwoFactorRequired(false);
              setTwoFactorCode('');
            }}
          >
            {mode === 'login' ? t('actions.register') : t('actions.alreadyHaveAccount')}
          </button>
        </div>
        )}
        {mode === 'login' && (
          <div style={{ marginTop: 6, textAlign: 'center' }}>
            <button className="link-btn" onClick={() => setTicketOpen(true)}>{t('support.link')}</button>
          </div>
        )}
        {/* Переключатель языка на самом входе: подписи — родные названия, читаются на любом языке. */}
        <div style={{ marginTop: 10, textAlign: 'center' }}>
          {LANGUAGES.map((l, i) => (
            <span key={l.code}>
              {i > 0 && ' · '}
              <button
                className="link-btn"
                type="button"
                aria-pressed={i18n.resolvedLanguage === l.code}
                style={i18n.resolvedLanguage === l.code ? { fontWeight: 700 } : undefined}
                onClick={() => void setLanguagePref(l.code)}
              >{l.nativeName}</button>
            </span>
          ))}
        </div>
      </div>

      {ticketOpen && (
        <div className="modal-backdrop" {...backdropClose(() => setTicketOpen(false))}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <h2>{t('support.title')}</h2>
            <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>
              {t('support.description')}
            </p>
            <div className="form">
              <input
                className="input" type="email" placeholder={t('fields.ticketEmail')} value={ticketEmail}
                onChange={(e) => setTicketEmail(e.target.value)} autoFocus
              />
              <textarea
                className="input" rows={4} style={{ resize: 'vertical' }} placeholder={t('fields.ticketDescription')}
                value={ticketMessage}
                onChange={(e) => setTicketMessage(e.target.value)}
              />
              {ticketError && <div className="error">{ticketError}</div>}
              {ticketMsg && <div style={{ fontSize: 13, color: 'var(--success)' }}>{ticketMsg}</div>}
              <button className="btn" disabled={ticketBusy} onClick={sendTicket}>
                {ticketBusy ? t('support.sending') : t('support.send')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
